
import { HttpStatusCode } from '@angular/common/http';
import { Component, OnInit, inject, signal, viewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { StyleClassModule } from 'primeng/styleclass';
import { finalize } from 'rxjs';
import { CaptchaComponent } from '../../../../../common/components/captcha/captcha.component';
import { AppConfigService } from '../../../../../common/config/app.config.service';
import { ComponentAccess } from '../../../../../common/decorators/component-access.decorator';
import { CommonRoute } from '../../../../../common/enums/common-route.enum';
import { CaptchaMessage } from '../../../../../common/enums/captcha-message.enum';
import { AuthenticationService } from '../../../../../common/services/authentication/authentication.service';
import { passwordMatchValidator } from '../../../../../common/validators/password-match.validator';
import { passwordStrengthValidator } from '../../../../../common/validators/password-strength.validator';
import { LayoutService } from '../../../../layout/service/app.layout.service';

/** The API's 404 for a reset link that ran out (10 minutes) or was already used. */
const EXPIRED_LINK_MESSAGE =
  'This link has expired or was already used. Use "Request a new one" below to get a fresh link.';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  templateUrl: './reset-password.component.html',
  imports: [
    ReactiveFormsModule,
    ButtonModule,
    PasswordModule,
    InputTextModule,
    CardModule,
    StyleClassModule,
    RouterModule,
    CaptchaComponent
],
})
@ComponentAccess('ResetPasswordComponent')
export class ResetPasswordComponent implements OnInit {
  readonly layoutService = inject(LayoutService);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly configService = inject(AppConfigService);
  private readonly messageService = inject(MessageService);

  newPasswordForm: FormGroup<{
    password: FormControl<string>;
    confirmPassword: FormControl<string>;
  }>;
  token: string | null = null;
  readonly resetSuccess = signal<boolean>(false);
  /** From the moment Reset is clicked: the browser check alone can take seconds. */
  readonly isSubmitting = signal<boolean>(false);

  // Captcha related properties
  readonly isCaptchaRequired = signal<boolean>(false);
  private readonly captcha = viewChild(CaptchaComponent);

  constructor() {
    this.newPasswordForm = new FormGroup(
      {
        password: new FormControl('', {
          nonNullable: true,
          validators: [Validators.required, passwordStrengthValidator()],
        }),
        confirmPassword: new FormControl('', {
          nonNullable: true,
          validators: [Validators.required],
        }),
      },
      { validators: passwordMatchValidator },
    );

    // Get token from the route parameters
    this.route.paramMap.subscribe((params) => {
      this.token = params.get('token');
    });
  }

  ngOnInit(): void {
    this.isCaptchaRequired.set(this.configService.isCaptchaEnabled);
  }

  passwordErrorText(): string | null {
    const control = this.newPasswordForm.get('password');
    if (control && control.invalid && (control.touched || control.dirty)) {
      if (control.errors?.['required']) {
        return 'New password is required.';
      }
      if (control.errors?.['passwordStrength']) {
        const strengthErrors = control.errors['passwordStrength'];
        const message = 'Password must ';
        const parts = [];
        if (strengthErrors.hasMinLength) {
          parts.push('be at least 8 characters long');
        }
        const detailParts = [];
        if (strengthErrors.hasUpperCase) {
          detailParts.push('an uppercase letter');
        }
        if (strengthErrors.hasLowerCase) {
          detailParts.push('a lowercase letter');
        }
        if (strengthErrors.hasNumeric) {
          detailParts.push('a number');
        }
        if (strengthErrors.hasSpecial) {
          detailParts.push('a special character');
        }
        if (detailParts.length > 0) {
          parts.push(`contain ${detailParts.join(', ')}`);
        }
        return message + parts.join(' and ') + '.';
      }
    }
    return null;
  }

  confirmPasswordErrorText(): string | null {
    const confirmPasswordControl = this.newPasswordForm.get('confirmPassword');

    if (
      confirmPasswordControl &&
      (confirmPasswordControl.touched || confirmPasswordControl.dirty)
    ) {
      if (confirmPasswordControl.errors?.['required']) {
        return 'Confirm new password is required.';
      }
    }
    if (
      this.newPasswordForm.errors?.['passwordMismatch'] &&
      (confirmPasswordControl?.touched || confirmPasswordControl?.dirty)
    ) {
      return 'Passwords do not match.';
    }
    if (confirmPasswordControl?.errors?.['passwordMismatch']) {
      return 'Passwords do not match.';
    }
    return null;
  }

  async onSubmit(): Promise<void> {
    if (this.newPasswordForm.valid && this.token && !this.isSubmitting()) {
      // Busy BEFORE the browser check, which can take seconds on its own.
      this.isSubmitting.set(true);

      // The captcha challenge runs on demand here (execute-on-submit), so
      // nothing refreshes or goes stale while the page sits open.
      const captchaOk = (await this.captcha()?.verify()) ?? false;
      if (this.isCaptchaRequired() && !captchaOk) {
        this.isSubmitting.set(false);
        this.messageService.add({
          severity: 'error',
          summary: CaptchaMessage.FailedSummary,
          detail: CaptchaMessage.Failed,
        });
        return;
      }

      const { password } = this.newPasswordForm.value;
      if (!password) {
        this.isSubmitting.set(false);
        return;
      }
      this.authenticationService
        .resetPassword(this.token, password)
        .pipe(finalize(() => this.isSubmitting.set(false)))
        .subscribe({
          next: () => {
            this.resetSuccess.set(true);
            this.newPasswordForm.disable();
            setTimeout(() => {
              this.router.navigate([CommonRoute.LOGIN]);
            }, 3000);
          },
          error: (err) => {
            this.resetSuccess.set(false);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail:
                err?.status === HttpStatusCode.NotFound
                  ? EXPIRED_LINK_MESSAGE
                  : err?.error?.message ||
                    'Failed to reset password. Please try again.',
            });
          },
        });
    }
  }

  navigateToLogin(): void {
    this.router.navigate([CommonRoute.LOGIN]);
  }

  navigateToRequestReset(): void {
    this.router.navigate([CommonRoute.REQUEST_PASSWORD_RESET]);
  }
}
